import React, { useCallback, useEffect, useState } from "react";
import { useReactiveVar } from "@apollo/client";
import { useLocation } from "react-router-dom";

import OllieChat, { OLLIE_AVATAR } from "components/ollie-chat/ollie-chat";

import { ollieAssistantOpenVar } from "state/reactive-vars/ai-chat-status-var";

import routeNames from "constants/route-names";

// The /ollie/chat page already shows this conversation full screen, so the launcher is hidden
// there: opening it would show a second copy of the same conversation.
const isChatPage = (pathname: string): boolean => pathname === routeNames.OLLIE_CHAT;

/*
Ollie on the inspection and plan review screens: a floating button in the lower right corner, and
the conversation in a panel above it. The Ollie layout renders this component, so it is available
on every screen that layout draws.

The open state and the current thread are kept in reactive vars instead of component state. Each
route mounts its own copy of the layout, so this component is unmounted and remounted on every
navigation, and local state would be lost.
*/
const OllieAssistant: React.FC = () => {
	const { pathname } = useLocation();
	const isOpen = useReactiveVar(ollieAssistantOpenVar);
	// Stays true after the panel is closed until its closing animation ends, so it can shrink back
	// into the launcher before it is removed.
	const [isPanelMounted, setIsPanelMounted] = useState(isOpen);

	useEffect(() => {
		if (isOpen) setIsPanelMounted(true);
	}, [isOpen]);

	const handleOnClickLauncher = useCallback(() => {
		ollieAssistantOpenVar(!ollieAssistantOpenVar());
	}, []);

	const handleOnClose = useCallback(() => {
		ollieAssistantOpenVar(false);
	}, []);

	const handleOnPanelAnimationEnd = useCallback(
		(event: React.AnimationEvent<HTMLDivElement>) => {
			// The messages inside the panel animate too, and their events bubble up to it.
			if (event.target !== event.currentTarget || isOpen) return;

			setIsPanelMounted(false);
		},
		[isOpen]
	);

	if (isChatPage(pathname)) {
		return null;
	}

	return (
		<>
			<button
				type="button"
				className="oa-launcher"
				onClick={handleOnClickLauncher}
				title={isOpen ? "Hide Ollie" : "Ask Ollie"}
				aria-label={isOpen ? "Hide Ollie" : "Ask Ollie"}
				aria-expanded={isOpen}
			>
				<span className="oa-launcher-pulse" aria-hidden="true" />
				<span className="oa-launcher-face">
					<img src={OLLIE_AVATAR} alt="" className="oa-launcher-avatar" />
				</span>
			</button>

			{(isOpen || isPanelMounted) && (
				<div
					className={`oa-panel${isOpen ? "" : " oa-panel-closing"}`}
					role="dialog"
					aria-label="Ollie"
					aria-hidden={!isOpen}
					onAnimationEnd={handleOnPanelAnimationEnd}
				>
					<OllieChat onClose={handleOnClose} />
				</div>
			)}
		</>
	);
};

export default OllieAssistant;
